import type { ServiceProvider } from '@blocksuite/affine/global/di';
import { MarkdownAdapter } from '@blocksuite/affine/shared/adapters';
import type { Store } from '@blocksuite/affine/store';
import type { Content, Heading, List, ListItem, Root } from 'mdast';

/**
 * Parses an Explore-mode markdown answer into a structured tree suitable for
 * rendering as an edgeless mind map. Handles both the legacy prompt template
 * (`### TL;DR / ### Evidence`) and the deep-research output
 * (`## Key Findings` with nested "Evidence strength: Strong" bullets and
 * `[PMID:xxx](url)` markdown-link citations).
 */

export type Grade = '🟢' | '🟡' | '🟠' | '🔴';
const GRADE_CHARS: Grade[] = ['🟢', '🟡', '🟠', '🔴'];

export type SectionKind =
  | 'findings'
  | 'papers'
  | 'consistency'
  | 'questions'
  | 'nextSteps'
  | 'sources'
  | 'other';

export type ExploreItem = {
  /** Full flattened text of the finding (headline + detail). Used for
   * uniqueness keys and for full-text fallback. */
  text: string;
  /** Short label (first bold span or first clause, trimmed to ~60 chars).
   * Used for compact mind-map node labels so the canvas doesn't blow up. */
  shortLabel: string;
  grade?: Grade;
  citations: string[];
};

export type ExploreSection = {
  title: string;
  kind: SectionKind;
  items: ExploreItem[];
};

export type ExploreCitation = {
  id: string;
  label: string;
  url?: string;
};

export type ExploreTree = {
  query: string;
  tldrMarkdown: string;
  sections: ExploreSection[];
  allCitations: Map<string, ExploreCitation>;
};

// Matches PMID:12345, DOI:10.xxxx/yyy, arXiv:2401.00001 with or without
// surrounding brackets. After MarkdownAdapter AST flattening, brackets and
// parens are stripped from `[PMID:123](url)` links — so we look for the bare
// token here.
const CITATION_TOKEN_REGEX =
  /(PMID:\s*\d+|DOI:\s*10\.[^\s\]()]+|arXiv:\s*[\w./-]+)/gi;

// Numeric reference like [[1]](url) or [1](url).
const NUMERIC_LINK_CITATION_REGEX = /\[\[?\d+\]?\]\(([^)]+)\)/g;

// "Evidence strength: Strong" / "(Strong)" / "— Strong —" patterns.
const GRADE_WORD_REGEX =
  /(?:(?:\bevidence\s+)?strength\s*:?\s*|[—–-]\s*|\(\s*)(Strong|Moderate|Weak|High|Low|Very\s+Low)(?:\s*[—–-]|\s*\))?/i;

export const parseExploreMarkdown = (
  answer: string,
  doc: Store,
  provider: ServiceProvider
): ExploreTree => {
  const tree: ExploreTree = {
    query: 'Research',
    tldrMarkdown: '',
    sections: [],
    allCitations: new Map(),
  };

  if (!answer) return tree;

  const transformer = doc.getTransformer();
  const markdown = new MarkdownAdapter(transformer, provider);
  const ast: Root = (markdown as unknown as {
    _markdownToAst(s: string): Root;
  })._markdownToAst(answer);

  if (!ast?.children?.length) return tree;

  const groups: Array<{ heading: Heading | null; children: Content[] }> = [
    { heading: null, children: [] },
  ];

  for (const child of ast.children) {
    if (child.type === 'heading' && child.depth <= 3) {
      if (child.depth === 1) {
        tree.query = plainText(child) || tree.query;
        continue;
      }
      groups.push({ heading: child, children: [] });
    } else {
      groups[groups.length - 1].children.push(child);
    }
  }

  for (const group of groups) {
    const title = group.heading ? plainText(group.heading) : '';
    const kind = sectionKindOf(title);

    if (isTldrTitle(title) || isSearchSummaryTitle(title)) {
      tree.tldrMarkdown =
        sliceOriginal(answer, group.children) ||
        group.children
          .filter(c => c.type === 'paragraph' || c.type === 'list')
          .map(c => plainText(c))
          .join('\n\n');
      continue;
    }

    if (!group.heading) continue;

    const items: ExploreItem[] = [];
    for (const node of group.children) {
      if (node.type === 'list') {
        for (const li of (node as List).children) {
          const item = listItemToExploreItem(li, tree.allCitations);
          if (item) items.push(item);
        }
      } else if (node.type === 'paragraph') {
        const raw = plainText(node);
        const item = textToExploreItem(raw, tree.allCitations);
        if (item) items.push(item);
      }
    }

    if (items.length === 0 && kind === 'other') continue;

    tree.sections.push({ title, kind, items });
  }

  if (!tree.tldrMarkdown) {
    const firstPara = ast.children.find(c => c.type === 'paragraph');
    if (firstPara) tree.tldrMarkdown = plainText(firstPara);
  }

  return tree;
};

function isTldrTitle(title: string): boolean {
  return /^\s*(tl;?dr|summary|overview|abstract)\s*$/i.test(title);
}

function isSearchSummaryTitle(title: string): boolean {
  return /^\s*(search\s+results?\s+summary)\s*$/i.test(title);
}

function sectionKindOf(title: string): SectionKind {
  const t = title.toLowerCase();
  // Notable Papers / Key Papers are study references — not graded findings.
  if (/\bnotable\s+paper|\bkey\s+paper|\brelated\s+paper/.test(t))
    return 'papers';
  if (/\b(key\s+)?(finding|evidence|result)/.test(t)) return 'findings';
  if (/\b(consistency|contradict|conflict)/.test(t)) return 'consistency';
  if (/\b(open\s+question|gap|unknown)/.test(t)) return 'questions';
  if (/\b(next\s+step|suggested\s+follow|recommendation|further)/.test(t))
    return 'nextSteps';
  if (/\b(source|reference|citation|bibliograph)/.test(t)) return 'sources';
  return 'other';
}

function plainText(node: Content | Heading): string {
  if (!('children' in node) || !node.children) return '';
  return (node.children as Content[])
    .map(child => {
      if (child.type === 'text') return child.value;
      if (child.type === 'inlineCode') return child.value;
      if ('children' in child) return plainText(child as Content);
      return '';
    })
    .join('');
}

function sliceOriginal(source: string, nodes: Content[]): string {
  if (!nodes.length) return '';
  const first = nodes[0].position?.start?.offset;
  const last = nodes[nodes.length - 1].position?.end?.offset;
  if (first == null || last == null) return '';
  return source.slice(first, last).trim();
}

/**
 * Flattens a list-item's content:
 *   - first paragraph is the "headline" (finding title or question body)
 *   - nested list (if any) contributes detail text, from which we extract
 *     evidence-strength words and additional citations
 *   - sub-list text is not appended to the visible node label (keeps the
 *     mindmap node readable); citations & grades are still harvested.
 */
function listItemToExploreItem(
  li: ListItem,
  store: Map<string, ExploreCitation>
): ExploreItem | null {
  const headlineParts: string[] = [];
  const detailParts: string[] = [];
  let boldPrefix = '';

  for (const c of li.children) {
    if (c.type === 'paragraph') {
      headlineParts.push(plainText(c));
      if (!boldPrefix) boldPrefix = firstStrongText(c) ?? '';
    } else if (c.type === 'list') {
      for (const sub of (c as List).children) {
        detailParts.push(plainText(sub));
      }
    } else if ('children' in c) {
      headlineParts.push(plainText(c as Content));
    }
  }

  const headline = headlineParts.join(' ').trim();
  const detail = detailParts.join(' | ');
  if (!headline && !detail) return null;

  const item = textToExploreItem(headline || detail, store);
  if (!item) return null;

  // Harvest grade + citations from the sub-list text too.
  if (detail) {
    const gradeFromDetail = extractGrade(detail);
    if (!item.grade && gradeFromDetail) item.grade = gradeFromDetail;
    harvestCitations(detail, store, item.citations);
  }

  item.shortLabel = pickShortLabel(boldPrefix, item.text);
  return item;
}

/**
 * Walk a paragraph AST looking for the first `strong` (bold) span and
 * return its flattened text. Explore findings are written as
 * "**Bold summary** explanatory prose" — the bold span is a great short
 * label for the mind-map node.
 */
function firstStrongText(node: Content): string | null {
  if (!('children' in node) || !node.children) return null;
  for (const child of node.children as Content[]) {
    if (child.type === 'strong') return plainText(child);
    if ('children' in child) {
      const nested = firstStrongText(child as Content);
      if (nested) return nested;
    }
  }
  return null;
}

function pickShortLabel(bold: string, fallback: string): string {
  const raw = (bold || fallback).trim();
  if (!raw) return fallback.trim();
  // Cut at first sentence boundary if it lands in a readable range.
  const sentenceEnd = raw.search(/[.!?](?:\s|$)/);
  let candidate =
    sentenceEnd >= 12 && sentenceEnd <= 80 ? raw.slice(0, sentenceEnd) : raw;
  if (candidate.length > 60) {
    candidate = candidate.slice(0, 57).trimEnd() + '…';
  }
  return candidate.trim();
}

function textToExploreItem(
  raw: string,
  store: Map<string, ExploreCitation>
): ExploreItem | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const grade = extractGrade(trimmed);
  const citations: string[] = [];
  let cleanText = trimmed;

  cleanText = cleanText.replace(NUMERIC_LINK_CITATION_REGEX, (_match, url: string) => {
    const id = `url:${url}`;
    if (!store.has(id)) {
      store.set(id, { id, label: shortenUrl(url), url });
    }
    if (!citations.includes(id)) citations.push(id);
    return '';
  });

  harvestCitations(cleanText, store, citations);
  cleanText = cleanText.replace(CITATION_TOKEN_REGEX, '');

  cleanText = cleanText
    .replace(new RegExp(GRADE_CHARS.join('|'), 'gu'), '')
    .replace(GRADE_WORD_REGEX, '')
    .replace(/\*{1,3}/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/^\s*[-—–]\s*|\s*[-—–]\s*$/g, '')
    .trim();

  if (!cleanText && citations.length === 0) return null;

  const finalText = cleanText || trimmed;
  return {
    text: finalText,
    shortLabel: pickShortLabel('', finalText),
    grade,
    citations,
  };
}

function extractGrade(text: string): Grade | undefined {
  for (const g of GRADE_CHARS) {
    if (text.includes(g)) return g;
  }
  const wordMatch = text.match(GRADE_WORD_REGEX);
  if (!wordMatch) return undefined;
  const word = wordMatch[1].toLowerCase().replace(/\s+/g, ' ');
  if (word === 'strong' || word === 'high') return '🟢';
  if (word === 'moderate') return '🟡';
  if (word === 'weak' || word === 'low') return '🟠';
  if (word === 'very low') return '🔴';
  return undefined;
}

function harvestCitations(
  text: string,
  store: Map<string, ExploreCitation>,
  collected: string[]
): void {
  const seen = new Set<string>();
  for (const match of text.matchAll(CITATION_TOKEN_REGEX)) {
    const canonical = canonicalizeToken(match[1]);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    if (!store.has(canonical)) {
      store.set(canonical, { id: canonical, label: canonical });
    }
    if (!collected.includes(canonical)) collected.push(canonical);
  }
}

function canonicalizeToken(token: string): string {
  const squished = token.replace(/\s+/g, '');
  if (/^pmid:/i.test(squished)) return `PMID:${squished.slice(5)}`;
  if (/^doi:/i.test(squished)) return `DOI:${squished.slice(4)}`;
  if (/^arxiv:/i.test(squished)) return `arXiv:${squished.slice(6)}`;
  return squished;
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url.length > 32 ? url.slice(0, 29) + '…' : url;
  }
}
